import { useTranslation } from "react-i18next";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Kbd } from "@/components/ui/kbd";
import { shortcutDefinitions } from "@/hooks/use-keyboard-shortcuts";

interface KeyboardShortcutsModalProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}

export function KeyboardShortcutsModal({ open, onOpenChange }: KeyboardShortcutsModalProps) {
	const { t } = useTranslation();

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-md">
				<DialogHeader>
					<DialogTitle>{t("shortcuts.title")}</DialogTitle>
					<DialogDescription>{t("common.actions")}</DialogDescription>
				</DialogHeader>

				<div className="space-y-6">
					{/* Navigation */}
					<div>
						<h4 className="mb-3 text-sm font-medium text-muted-foreground">
							{t("shortcuts.navigation")}
						</h4>
						<div className="space-y-2">
							{shortcutDefinitions.navigation.map((shortcut) => (
								<ShortcutRow
									key={shortcut.description}
									keys={shortcut.keys}
									description={t(shortcut.description)}
								/>
							))}
						</div>
					</div>

					{/* Actions */}
					<div>
						<h4 className="mb-3 text-sm font-medium text-muted-foreground">
							{t("shortcuts.actions")}
						</h4>
						<div className="space-y-2">
							{shortcutDefinitions.actions.map((shortcut) => (
								<ShortcutRow
									key={shortcut.description}
									keys={shortcut.keys}
									description={t(shortcut.description)}
								/>
							))}
						</div>
					</div>

					{/* General */}
					<div>
						<h4 className="mb-3 text-sm font-medium text-muted-foreground">
							{t("shortcuts.general")}
						</h4>
						<div className="space-y-2">
							{shortcutDefinitions.general.map((shortcut) => (
								<ShortcutRow
									key={shortcut.description}
									keys={shortcut.keys}
									description={t(shortcut.description)}
								/>
							))}
						</div>
					</div>
				</div>
			</DialogContent>
		</Dialog>
	);
}

interface ShortcutRowProps {
	keys: string[];
	description: string;
}

function ShortcutRow({ keys, description }: ShortcutRowProps) {
	return (
		<div className="flex items-center justify-between">
			<span className="text-sm">{description}</span>
			<div className="flex items-center gap-1">
				{keys.map((key, index) => (
					<span key={key} className="flex items-center gap-1">
						<Kbd>{key}</Kbd>
						{index < keys.length - 1 && <span className="text-muted-foreground">+</span>}
					</span>
				))}
			</div>
		</div>
	);
}
